// Warteschlange fuer Aenderungen, die im Funkloch abgeschickt wurden (#322).
//
// Die Lese-Seite ist seit #229 offline-tauglich: Der Service Worker liefert
// gewhitelistete GETs aus dem Cache. Die Schreib-Seite war es nicht -- der
// Service Worker laesst alles ausser GET ungefiltert durch (`sw.js`, method
// !== 'GET' -> return), ein abgeschickter Kommentar scheiterte also auf
// Netzebene, und der Text war weg.
//
// BEWUSST NUR KOMMENTARE, und nur die ueber das Kommentarfeld abgeschickten.
// Eine allgemeine Warteschlange fuer alle Mutationen waere gefaehrlich: Ein
// nachgesendetes PUT auf ein Ticket wuerde die Aenderung eines Kollegen
// ueberschreiben, die in der Zwischenzeit entstanden ist, und ein
// nachgesendetes Loeschen trifft moeglicherweise etwas anderes als gemeint.
// Ein Kommentar dagegen kommt nur hinzu; er nimmt nichts zurueck.
//
// Kein Background-Sync des Service Workers: Das gibt es auf iOS nicht, und die
// PWA laeuft dort. Gesendet wird beim `online`-Ereignis und beim Start.

export const SPEICHER_SCHLUESSEL = 'offlineWarteschlange.v1';
export const MAX_EINTRAEGE = 50;
export const MAX_ALTER_MS = 7 * 24 * 60 * 60 * 1000; // eine Woche

/**
 * Trennt 'die Anfrage kam nie an' von 'der Server hat abgelehnt'.
 *
 * Nur der erste Fall gehoert in die Warteschlange. Ein 403 oder 400 wuerde
 * beim Nachsenden genauso scheitern -- und ein spaeter wiederholtes 403 waere
 * eine Rechtepruefung, die man umgeht.
 */
export const istNetzfehler = (error) => {
  if (!error) return false;
  // Der Service Worker meldet seinen Offline-Fall als 503 mit Kennung (#229).
  if (error.response?.status === 503 && error.response?.data?.error === 'offline') return true;
  // Sonst gilt: eine Antwort gab es nicht, ein Versuch schon.
  if (error.response) return false;
  return error.code === 'ERR_NETWORK' || !!error.request;
};

// Wie weit die Dublettenpruefung vor dem Einreihen sucht -- die Uhren von
// Browser und Server laufen nicht zwingend gleich.
export const UHREN_TOLERANZ_MS = 5 * 60 * 1000;

/**
 * Steht der Kommentar schon am Ticket?
 *
 * Gebraucht, weil ein Netzfehler NICHT heisst, dass der Server nichts bekommen
 * hat: Eine Verbindung, die nach dem Schreiben abreisst, sieht von aussen
 * genauso aus wie eine, die nie zustande kam. Nachgesehen wird nur im
 * Zeitfenster um das Einreihen herum -- ohne diese Schranke wuerde ein frueher
 * schon einmal geschriebenes 'erledigt' den neuen Kommentar verschlucken.
 */
export const istDublette = (eintrag, kommentare, toleranzMs = UHREN_TOLERANZ_MS) => {
  if (!eintrag || !Array.isArray(kommentare)) return false;
  const ab = eintrag.erstelltAm - toleranzMs;
  return kommentare.some(k => {
    if ((k?.content || '') !== eintrag.inhalt) return false;
    const erstellt = k?.createdAt ? new Date(k.createdAt).getTime() : NaN;
    return Number.isNaN(erstellt) ? false : erstellt >= ab;
  });
};

const jetzt = () => Date.now();

const lade = () => {
  try {
    const roh = window.localStorage.getItem(SPEICHER_SCHLUESSEL);
    const liste = roh ? JSON.parse(roh) : [];
    return Array.isArray(liste) ? liste : [];
  } catch (_) {
    return [];
  }
};

const sichere = (liste) => {
  try {
    window.localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(liste));
    return true;
  } catch (_) {
    // Privater Modus oder voller Speicher: Dann gibt es eben keine
    // Warteschlange. Der Aufrufer erfaehrt es am Rueckgabewert.
    return false;
  }
};

/** Liest die Warteschlange und wirft dabei zu alte Eintraege weg. */
export const lies = (zeitpunkt = jetzt()) => {
  const liste = lade();
  const frisch = liste.filter(e => e && e.erstelltAm && (zeitpunkt - e.erstelltAm) < MAX_ALTER_MS);
  if (frisch.length !== liste.length) sichere(frisch);
  return frisch;
};

export const anzahl = () => lies().length;

/**
 * Nimmt einen Kommentar in die Warteschlange auf.
 * @returns {object|null} der gespeicherte Eintrag, oder null wenn nicht gespeichert werden konnte
 */
export const reiheKommentarEin = ({ ticketId, inhalt, isInternal = false }, zeitpunkt = jetzt()) => {
  if (!ticketId || !inhalt) return null;
  const liste = lies(zeitpunkt);
  const eintrag = {
    id: `${zeitpunkt}-${Math.random().toString(36).slice(2, 8)}`,
    art: 'kommentar',
    ticketId,
    inhalt,
    isInternal: !!isInternal,
    erstelltAm: zeitpunkt
  };
  // Der aelteste faellt raus, wenn der Deckel erreicht ist -- ein voller
  // Speicher darf den neuen Kommentar nicht verhindern.
  const neu = [...liste, eintrag].slice(-MAX_EINTRAEGE);
  return sichere(neu) ? eintrag : null;
};

export const entferne = (id) => sichere(lies().filter(e => e.id !== id));

export const leere = () => sichere([]);

/**
 * Sendet die Warteschlange ab.
 *
 * Die beiden Zugriffe auf den Server werden hereingereicht, damit dieses Modul
 * nichts importiert -- apiService importiert es selbst, ein Rueckbezug waere
 * ein Ring.
 *
 * @param {(eintrag:object) => Promise<any>} sende
 * @param {(eintrag:object) => Promise<boolean>} [istSchonDa] optionale
 *        Dublettenpruefung, siehe unten
 * @returns {Promise<{gesendet:number, verworfen:Array, verblieben:number}>}
 */
export const sendeWarteschlange = async (sende, istSchonDa = null) => {
  const liste = lies();
  const ergebnis = { gesendet: 0, verworfen: [], verblieben: 0 };
  if (liste.length === 0) return ergebnis;

  for (const eintrag of liste) {
    try {
      // Ein Netzfehler heisst nicht zwingend, dass der Server nichts bekommen
      // hat -- eine abgerissene Verbindung nach dem Schreiben sieht genauso
      // aus. Deshalb vor dem Nachsenden nachsehen, ob der Kommentar schon
      // dasteht; sonst entstuende eine Dublette.
      if (istSchonDa && await istSchonDa(eintrag)) {
        entferne(eintrag.id);
        ergebnis.gesendet++;
        continue;
      }
      await sende(eintrag);
      entferne(eintrag.id);
      ergebnis.gesendet++;
    } catch (fehler) {
      if (istNetzfehler(fehler)) {
        // Immer noch kein Netz: stehenlassen und beim naechsten Mal erneut.
        break;
      }
      // Der Server hat abgelehnt (Ticket archiviert, geloescht, keine Rechte).
      // Wiederholen wuerde nichts aendern, also raus -- aber sichtbar.
      entferne(eintrag.id);
      ergebnis.verworfen.push({ eintrag, grund: fehler?.response?.data?.error || fehler?.message || 'unbekannt' });
    }
  }
  ergebnis.verblieben = lies().length;
  return ergebnis;
};
